import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { activeDbName, db, knownBusinesses, newBusinessDbName } from '../../lib/db.ts';
import { BusinessLogo } from '../../app/BusinessLogo.tsx';
import { switchBusiness } from '../../services/session.ts';
import { Alert, Button, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';

/**
 * Botón «Cambiar de negocio»: elige otro negocio guardado en este dispositivo o agrega uno nuevo
 * (lo habilita el dueño de ese negocio). Cada negocio tiene su base local aparte.
 */
export function BusinessSwitcherButton({ className, variant = 'link' }: { className?: string; variant?: 'link' | 'button' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === 'link' ? (
        <button type="button" className={className ?? 'font-medium text-brand underline'} onClick={() => setOpen(true)}>
          Cambiar de negocio
        </button>
      ) : (
        <Button variant="ghost" block className={className} onClick={() => setOpen(true)}>
          Cambiar de negocio
        </Button>
      )}
      {/* Fuera del botón: desde la barra lateral (oscura) el diálogo heredaría sus colores. */}
      {open && createPortal(<BusinessSwitcherDialog onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function BusinessSwitcherDialog({ onClose }: { onClose: () => void }) {
  const active = activeDbName();
  const others = knownBusinesses().filter((b) => b.db !== active);
  const pending = useLiveQuery(() => db.outbox.where('status').equals('pending').count(), [], 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async (target: string) => {
    setBusy(true);
    setError(null);
    try {
      await switchBusiness(target);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Cambiar de negocio">
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">
          Cada negocio se guarda aparte en este dispositivo. Al cambiar, se cierra la sesión de este negocio y se abre el otro.
        </p>
        {pending > 0 && (
          <Alert tone="warning">
            En este negocio hay {pending} {pending === 1 ? 'cambio' : 'cambios'} sin enviar. No se pierden: se intentan enviar ahora y, si no hay
            conexión, se envían cuando vuelvas a entrar a este negocio.
          </Alert>
        )}
        {others.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {others.map((b) => (
              <li key={b.db}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void go(b.db)}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-slate-50 disabled:opacity-50"
                >
                  <BusinessLogo name={b.name} logoPath={b.logo_path} className="size-9 rounded-lg" />
                  <span className="min-w-0 flex-1 truncate font-medium">{b.name ?? 'Negocio sin terminar de configurar'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        <div className="flex flex-wrap justify-between gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="secondary" loading={busy} onClick={() => void go(newBusinessDbName())}>
            Agregar otro negocio
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          «Agregar otro negocio» lo tiene que habilitar el dueño de ese negocio, entrando con su email y contraseña. Cada negocio cuenta como un dispositivo
          más en su plan.
        </p>
      </div>
    </Dialog>
  );
}
